/* eslint react-hooks/exhaustive-deps: "warn" */
import { type MouseEvent, useEffect, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './DataLicensingRedistributionAiTraining.module.scss';

type Translate = (key: string) => string;

const getGrants = (t: Translate): string[] => [
  t('Pretraining'),
  t('Fine-tuning'),
  t('Evaluation'),
  t('Retrieval'),
  t('Synthetic data'),
];

export const DataLicensingRedistributionAiTraining = () => {
  const { t } = useInt();
  const grants = getGrants(t);
  const headRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const revealables = [headRef.current, panelRef.current].filter((el): el is HTMLDivElement => Boolean(el));

    if (!revealables.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.revealed);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 },
    );

    revealables.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleSpotlight = (e: MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - rect.top}px`);
  };

  return (
    <Section background="#0E1526" className={styles.root}>
      <div className={styles.glow} aria-hidden="true" />

      <div ref={headRef} className={cn(styles.head, styles.reveal)}>
        <TitleBlock
          theme="dark"
          align="left"
          title={t('Train on it, with a licence that says so')}
          description={t(
            'Registry data suits model work: structured, jurisdictionally diverse, factual, and dated at source. Permission is the part that is normally missing.',
          )}
        />
      </div>

      <div
        ref={panelRef}
        className={cn(styles.panel, styles.reveal)}
        style={{ transitionDelay: '120ms' }}
        onMouseMove={handleSpotlight}
      >
        <span className={styles.spotlight} aria-hidden="true" />
        <span className={cn(styles.corner, styles.cornerTl)} aria-hidden="true" />
        <span className={cn(styles.corner, styles.cornerBr)} aria-hidden="true" />

        <span className={styles.badge}>
          <span className={styles.badgeDot} aria-hidden="true" />
          {t('Granted in writing')}
        </span>

        <p className={styles.statement}>
          {t('Pretraining. Fine-tuning. Evaluation. Retrieval. Synthetic data.')}{' '}
          <span className={styles.key}>{t('All five, granted in writing.')}</span>
        </p>

        <div className={styles.grants}>
          {grants.map((grant) => (
            <span key={grant} className={styles.grant}>
              <span className={styles.tick} aria-hidden="true">
                ✓
              </span>
              {grant}
            </span>
          ))}
        </div>
      </div>
    </Section>
  );
};
